import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { colors } from '../styles/colors.ts';

const Container = styled.div`
    border-radius: 1rem;
  position: relative;
  width: 100%;
  height: ${props => props.height || '400px'};
  overflow: hidden;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
`;

const ImageWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const BeforeImage = styled.img`
    pointer-events: none;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    `;

const AfterImage = styled.img`
    pointer-events: none;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  clip-path: ${props => `inset(0 0 0 ${props.position}%)`};
`;

const Slider = styled.div`
  position: absolute;
  top: 0;
  left: ${props => props.position}%;
  width: 4px;
  height: 100%;
  background: ${(colors.white)};
  border: solid 1px ${(colors.neutral[100])};
  cursor: ew-resize;
  transform: translateX(-50%);
  transition: all ease;
  z-index: 2;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 2rem;
    height: 2rem;
    background: ${(colors.white)};
    border: solid 1px ${(colors.neutral[100])};
    border-radius: 50%;
    box-shadow: 0 2px 1rem ${(colors.black)}20;
    pointer-events: none;
  }

  & button {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 2rem;
    height: 2rem;
    background: ${(colors.white)};
    border: solid 1px ${(colors.neutral[100])};
    border-radius: 50%;
    box-shadow: 0 2px 1rem ${(colors.black)}20;
    cursor: ew-resize;
    padding: 0;
    z-index: 3;
  }
`;

const BeforeAfter = ({ beforeImage, afterImage, height, beforeMobile, afterMobile, mobileHeight }) => {
  const [position, setPosition] = useState(50);
  const containerRef = useRef(null);
  const isDragging = useRef(false);

  // Add media query check for mobile images
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleMouseDown = () => {
    isDragging.current = true;
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const handleMove = (clientX) => {
    if (!isDragging.current || !containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const newPosition = ((clientX - containerRect.left) / containerRect.width) * 100;
    setPosition(Math.min(Math.max(newPosition, 0), 100));
  };

  const handleMouseMove = useCallback((e) => {
    handleMove(e.clientX);
  }, []);

  const handleTouchMove = useCallback((e) => {
    handleMove(e.touches[0].clientX);
  }, []);

  useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('touchend', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove);

    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('touchend', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, [handleMouseMove, handleTouchMove]);

  return (
    <Container ref={containerRef} height={isMobile ? mobileHeight : height}>
      <ImageWrapper>
        <BeforeImage src={isMobile && beforeMobile ? beforeMobile : beforeImage} alt="Before" />
        <AfterImage 
          src={isMobile && afterMobile ? afterMobile : afterImage} 
          alt="After" 
          position={position} 
        />
      </ImageWrapper>
      <Slider 
        position={position}
        onMouseDown={handleMouseDown}
        onTouchStart={handleMouseDown}
      >
        <button 
          onMouseDown={handleMouseDown}
          onTouchStart={handleMouseDown}
          aria-label="Drag to compare images"
        />
      </Slider>
    </Container>
  );
};

export default BeforeAfter;
